import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_API_URL } from "../../../../utils/consts";
import { usePermissions } from "../../../../utils/usePermissions";
import { isSuperAdmin } from "../../../../utils/permissions";
import { useAuth } from "../../../../utils/useAuth";
import { useSelector } from "react-redux";
import "./UserSuppliers.scss";

interface Props {
  userDetails: any;
}

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #f97316, #ec4899)",
  "linear-gradient(135deg, #3b82f6, #8b5cf6)",
  "linear-gradient(135deg, #10b981, #3b82f6)",
  "linear-gradient(135deg, #f59e0b, #ef4444)",
  "linear-gradient(135deg, #8b5cf6, #ec4899)",
  "linear-gradient(135deg, #06b6d4, #3b82f6)",
];

const getGradient = (seed: string) =>
  AVATAR_GRADIENTS[(seed?.charCodeAt(seed.length - 1) ?? 0) % AVATAR_GRADIENTS.length];

const getInitials = (naziv = "") =>
  naziv.split(" ").slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase();

const UserSuppliers: React.FC<Props> = ({ userDetails }) => {
  const navigate = useNavigate();
  const authUser: any = useAuth();
  const userData = useSelector((state: any) => state.userData.user);
  const { hasPermission } = usePermissions();
  const isSuper = isSuperAdmin(userData?.roles || authUser?.roles);
  const canOpenProfile = hasPermission("suppliers:open-profile") || isSuper;

  const [suppliers, setSuppliers] = useState<any[] | null>(null);

  const clientId =
    typeof userDetails?.user?.clientId === "object"
      ? userDetails?.user?.clientId?._id
      : userDetails?.user?.clientId;
  const userId = userDetails?.user?._id;

  const enabledFeatures: string[] =
    userDetails?.user?.clientId?.settings?.enabledFeatures ?? [];
  const suppliersEnabled = isSuper || enabledFeatures.includes("suppliers");

  useEffect(() => {
    if (!clientId || !userId || !suppliersEnabled) return;
    axios
      .get(`${BASE_API_URL}/${clientId}/suppliers/by-user/${userId}`)
      .then((res) => setSuppliers(res.data))
      .catch(() => setSuppliers([]));
  }, [clientId, userId, suppliersEnabled]);

  if (!suppliersEnabled) return null;

  return (
    <div className="up-card">
      <div className="up-card-head">
        <span className="up-card-ic">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 7h11v10H3zM14 10h4l3 3v4h-7" />
            <circle cx="7" cy="18" r="1.6" />
            <circle cx="17" cy="18" r="1.6" />
          </svg>
        </span>
        <div className="up-card-titles">
          <h3 className="up-card-title">Dobavljači</h3>
          <div className="up-card-sub">
            Dobavljači za koje je zaposleni zadužen
            {suppliers !== null && suppliers.length > 0 && (
              <span className="us-count">{suppliers.length}</span>
            )}
          </div>
        </div>
      </div>

      <div className="up-card-body">
        {suppliers === null ? (
          <div className="us-loading">Učitavanje…</div>
        ) : suppliers.length === 0 ? (
          <div className="us-empty">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 7h11v10H3zM14 10h4l3 3v4h-7" />
              <circle cx="7" cy="18" r="1.6" />
              <circle cx="17" cy="18" r="1.6" />
            </svg>
            <span>Nema dodijeljenih dobavljača</span>
          </div>
        ) : (
          <div className="us-list">
            {suppliers.map((sup: any) => (
              <div
                key={sup._id}
                className={`us-row${canOpenProfile ? " us-row--clickable" : ""}`}
                onClick={() => canOpenProfile && navigate(`/suppliers/${sup._id}`)}
              >
                <div
                  className="us-avatar"
                  style={{ background: getGradient(sup._id || sup.naziv || "") }}
                >
                  {getInitials(sup.naziv)}
                </div>
                <div className="us-info">
                  <span className="us-name">{sup.naziv}</span>
                  {sup.kontaktOsoba && (
                    <span className="us-meta">{sup.kontaktOsoba}</span>
                  )}
                </div>
                {sup.datumNarudzbe && (
                  <span className="us-badge">{sup.datumNarudzbe}</span>
                )}
                {canOpenProfile && (
                  <svg className="us-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSuppliers;
